import { RouteTracker } from "@/components/Analytics/RouteTracker";
import { Button } from "@/components/ui/button";
import { useSidebar } from "@/contexts/SidebarContext";
import { AssistantLauncher } from "@/components/Assistant/AssistantLauncher";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { Outlet } from "react-router";
import Sidebar from "../Sidebar";

const TROIA_WIDGET_SCRIPT_ID = "troia-widget-script";

const AppLayout = () => {
  const { isSidebarOpen, isMobile } = useSidebar();
  const {
    token,
    permissionStatus,
    requestPermission,
    setupForegroundMessages,
    isSupported,
  } = usePushNotifications();
  const [showPrompt, setShowPrompt] = useState(false);

  // Show prompt after delay if no token and permission is 'default'
  useEffect(() => {
    if (!isSupported) return;

    const timer = setTimeout(() => {
      if (!token && Notification.permission === "default") {
        setShowPrompt(true);
      }
    }, 5000); // Show after 5 seconds
    return () => clearTimeout(timer);
  }, [token, isSupported]);

  const handleRequestPermission = async () => {
    await requestPermission();
    setShowPrompt(false);
  };

  const handleDisableNotifications = () => {
    setShowPrompt(false);
  };

  // Setup foreground messages when permission is granted
  useEffect(() => {
    if (permissionStatus === "granted" && token) {
      setupForegroundMessages();
    }
  }, [permissionStatus, token, setupForegroundMessages]);

  useEffect(() => {
    const existingScript = document.getElementById(TROIA_WIDGET_SCRIPT_ID);
    if (existingScript) return;

    (
      window as Window & { troiaWidgetConfig?: Record<string, unknown> }
    ).troiaWidgetConfig = {
      channelId: "6984de3d0473ff9fd49b7f1c",
      token: "28afba2e7ac8a5518a363e517f39a4679c0802e0552561d609f562641955dcd9",
      apiUrl: "https://backend.troiachat.com",
      socketUrl: "https://backend.troiachat.com",
    };

    const script = document.createElement("script");
    script.id = TROIA_WIDGET_SCRIPT_ID;
    script.src = "https://widget.troiachat.com/widget.umd.js";
    script.async = true;
    document.head.appendChild(script);
  }, []);

  return (
    <div className="min-h-screen bg-background transition-colors">
      <RouteTracker />
      <Sidebar />
      <main
        className={`transition-all duration-300 ease-in-out p-4 lg:p-4 pt-20 lg:pt-8 min-h-screen ${
          isMobile ? "" : isSidebarOpen ? "lg:ml-72" : "lg:ml-6"
        }`}
      >
        <Outlet />
      </main>

      {isSupported &&
        showPrompt &&
        !token &&
        Notification.permission === "default" && (
          <div className="fixed bottom-4 right-4 p-8 bg-background text-foreground shadow-lg rounded-lg border dark:border-gray-300">
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              onClick={handleDisableNotifications}
              aria-label="Fechar"
            >
              <X size={16} />
            </button>

            <p className="mb-2">
              Você gostaria de receber notificações na plataforma em tempo real?
            </p>
            <div className="flex space-x-2">
              <Button
                variant="default"
                className="dark:border dark:border-white"
                onClick={handleRequestPermission}
              >
                Habilitar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDisableNotifications}
              >
                Desabilitar
              </Button>
            </div>
          </div>
        )}
      <AssistantLauncher />
    </div>
  );
};

export default AppLayout;
